import React, { useEffect, useRef, useState } from 'react';

// Walkthrough Editor v2 — rich-text editor (BL-143).
// Self-contained WYSIWYG on the browser's contentEditable + execCommand — no
// Quill, no npm dependency, no external CSS (styled by WalkthroughEditorV2.scss).
// Uncontrolled after mount; the call site keys it by step id so it reloads with
// fresh content when the active step changes. Emits sanitized-on-render HTML.
//
// The {} Token button inserts a personalization placeholder (e.g. {{first_name}})
// as plain text. It renders literally to learners until runtime token resolution
// ships in a later phase — see BL-080/081 for the Paths token engine we'll reuse.

const exec = (command, value) => {
  try { document.execCommand(command, false, value); } catch (e) { /* no-op */ }
};

const TOKENS = [
  { label: 'First name', token: 'first_name' },
  { label: 'Last name', token: 'last_name' },
  { label: 'Full name', token: 'full_name' },
  { label: 'Email', token: 'email' },
  { label: 'Company', token: 'company_name' }
];

const Icon = ({ d, fill }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {d}
  </svg>
);
const BoldI = () => <Icon d={<path d="M7 5h6a3.5 3.5 0 0 1 0 7H7zM7 12h7a3.5 3.5 0 0 1 0 7H7z" />} />;
const ItalicI = () => <Icon d={<><line x1="19" y1="5" x2="11" y2="5" /><line x1="13" y1="19" x2="5" y2="19" /><line x1="15" y1="5" x2="9" y2="19" /></>} />;
const UnderlineI = () => <Icon d={<><path d="M7 4v6a5 5 0 0 0 10 0V4" /><line x1="5" y1="20" x2="19" y2="20" /></>} />;
const BulletI = () => <Icon d={<><line x1="9" y1="6" x2="20" y2="6" /><line x1="9" y1="12" x2="20" y2="12" /><line x1="9" y1="18" x2="20" y2="18" /><line x1="4" y1="6" x2="4" y2="6" /><line x1="4" y1="12" x2="4" y2="12" /><line x1="4" y1="18" x2="4" y2="18" /></>} />;
const NumberI = () => <Icon d={<><line x1="10" y1="6" x2="20" y2="6" /><line x1="10" y1="12" x2="20" y2="12" /><line x1="10" y1="18" x2="20" y2="18" /><path d="M4 4l1 0 0 5" /><path d="M4 9l2 0" /></>} />;
const LinkI = () => <Icon d={<><path d="M10 13a5 5 0 0 0 7.1 0l2-2a5 5 0 0 0-7.1-7.1l-1 1" /><path d="M14 11a5 5 0 0 0-7.1 0l-2 2a5 5 0 0 0 7.1 7.1l1-1" /></>} />;

const isBlank = (html) => !html || html === '<br>' || html === '<p><br></p>';

const WalkthroughRichText = ({ value, onChange, placeholder = 'Write instructions…', minHeight = 120, showToken = true }) => {
  const bodyRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const savedRange = useRef(null);
  const [tokenOpen, setTokenOpen] = useState(false);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Set initial content once (uncontrolled thereafter to preserve the caret).
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.innerHTML = typeof value === 'string' ? value : '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the token menu on any outside click.
  useEffect(() => {
    if (!tokenOpen) return undefined;
    const close = () => setTokenOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [tokenOpen]);

  const emit = () => {
    if (!bodyRef.current || !onChangeRef.current) return;
    const html = bodyRef.current.innerHTML;
    onChangeRef.current(isBlank(html) ? '' : html);
  };

  const run = (command) => (e) => {
    e.preventDefault();
    if (bodyRef.current) bodyRef.current.focus();
    exec(command);
    emit();
  };

  const addLink = (e) => {
    e.preventDefault();
    if (bodyRef.current) bodyRef.current.focus();
    const url = window.prompt('Link URL', 'https://');
    if (url) exec('createLink', url);
    emit();
  };

  const rememberCaret = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRange.current = sel.getRangeAt(0);
  };

  const insertToken = (token) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (bodyRef.current) bodyRef.current.focus();
    const sel = window.getSelection();
    if (savedRange.current && sel) { sel.removeAllRanges(); sel.addRange(savedRange.current); }
    exec('insertText', `{{${token}}}`);
    setTokenOpen(false);
    emit();
  };

  return (
    <div className="un-we-rte">
      <div className="un-we-rte__bar">
        <button type="button" title="Bold" aria-label="Bold" onMouseDown={run('bold')}><BoldI /></button>
        <button type="button" title="Italic" aria-label="Italic" onMouseDown={run('italic')}><ItalicI /></button>
        <button type="button" title="Underline" aria-label="Underline" onMouseDown={run('underline')}><UnderlineI /></button>
        <span className="un-we-rte__sep" />
        <button type="button" title="Bulleted list" aria-label="Bulleted list" onMouseDown={run('insertUnorderedList')}><BulletI /></button>
        <button type="button" title="Numbered list" aria-label="Numbered list" onMouseDown={run('insertOrderedList')}><NumberI /></button>
        <span className="un-we-rte__sep" />
        <button type="button" title="Add link" aria-label="Add link" onMouseDown={addLink}><LinkI /></button>
        {showToken && (
          <span className="un-we-rte__tokenwrap">
            <button
              type="button"
              className="un-we-rte__token"
              title="Insert a personalization token"
              onMouseDown={(e) => { e.preventDefault(); rememberCaret(); }}
              onClick={(e) => { e.stopPropagation(); setTokenOpen((o) => !o); }}
            >
              {'{ }'} Token
            </button>
            {tokenOpen && (
              <div className="un-we-rte__tokens" onClick={(e) => e.stopPropagation()}>
                {TOKENS.map((t) => (
                  <button key={t.token} type="button" onMouseDown={insertToken(t.token)}>
                    <span className="l">{t.label}</span>
                    <span className="c">{`{{${t.token}}}`}</span>
                  </button>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
      <div
        ref={bodyRef}
        className="un-we-rte__body"
        contentEditable
        suppressContentEditableWarning
        data-ph={placeholder}
        style={{ minHeight }}
        onInput={emit}
        onBlur={emit}
        onKeyUp={rememberCaret}
        onMouseUp={rememberCaret}
      />
    </div>
  );
};

export default WalkthroughRichText;
